import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date string for display
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a date for API requests (ISO format)
 */
export function formatDateForApi(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Transform snake_case object keys to camelCase
 */
export function snakeToCamelCase<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[camelKey] = snakeToCamelCase(value as Record<string, unknown>)
    } else if (Array.isArray(value)) {
      result[camelKey] = value.map((item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item)
          ? snakeToCamelCase(item as Record<string, unknown>)
          : item
      )
    } else {
      result[camelKey] = value
    }
  }

  return result as T
}

/**
 * Derive initials from a name string for avatar fallbacks.
 * Returns up to 2 uppercase characters.
 */
export function getInitials(name: string | null | undefined, fallback?: string): string {
  if (!name?.trim()) {
    return fallback?.charAt(0).toUpperCase() || 'U';
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
}

/**
 * Pass a photo URL through a scheme allow-list before binding it to an
 * <img src=...>. Local thumbnails come from URL.createObjectURL (blob:) and
 * server photos come from Firebase Storage signed URLs (https:); anything
 * else is dropped. Closes the DOM-XSS surface that static analyzers flag on
 * useState → src dataflows.
 */
export function safePhotoUrl(url: string | null | undefined): string {
  if (!url) return ''
  return url.startsWith('blob:') || url.startsWith('https://') ? url : ''
}

/**
 * Transform camelCase object keys to snake_case
 */
export function camelToSnakeCase<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[snakeKey] = camelToSnakeCase(value as Record<string, unknown>)
    } else if (Array.isArray(value)) {
      result[snakeKey] = value.map((item) =>
        item !== null && typeof item === 'object' && !Array.isArray(item)
          ? camelToSnakeCase(item as Record<string, unknown>)
          : item
      )
    } else {
      result[snakeKey] = value
    }
  }

  return result as T
}
